import { getSupabaseClient } from '../../lib/supabase'
import type { ReadinessAction, ReadinessDashboard, ReadinessResponse } from './readiness-types'

export async function getReadinessDashboard(propertyId: string): Promise<ReadinessDashboard> {
  const supabase = getSupabaseClient()
  const [assessmentResult, categoriesResult, questionsResult] = await Promise.all([
    supabase.from('readiness_assessments').select('*').eq('property_id', propertyId).maybeSingle(),
    supabase.from('readiness_categories').select('*').order('display_order'),
    supabase.from('readiness_questions').select('*').order('display_order'),
  ])
  if (assessmentResult.error) throw assessmentResult.error
  if (categoriesResult.error) throw categoriesResult.error
  if (questionsResult.error) throw questionsResult.error

  let answers: ReadinessDashboard['answers'] = []
  let actions: ReadinessDashboard['actions'] = []
  if (assessmentResult.data) {
    const [answersResult, actionsResult] = await Promise.all([
      supabase.from('readiness_answers').select('*').eq('assessment_id', assessmentResult.data.id),
      supabase.from('readiness_actions').select('*').eq('assessment_id', assessmentResult.data.id).order('created_at'),
    ])
    if (answersResult.error) throw answersResult.error
    if (actionsResult.error) throw actionsResult.error
    answers = answersResult.data
    actions = actionsResult.data
  }
  return { assessment: assessmentResult.data, categories: categoriesResult.data, questions: questionsResult.data, answers, actions }
}

export async function startAssessment(propertyId: string): Promise<string> {
  const { data, error } = await getSupabaseClient().rpc('start_readiness_assessment', { target_property_id: propertyId })
  if (error) throw error
  return data
}

export async function saveAnswer(input: {
  assessmentId: string
  questionId: string
  response: ReadinessResponse
  evidenceUrl: string
  comments: string
  userId: string
}): Promise<void> {
  const { error } = await getSupabaseClient().from('readiness_answers').upsert({
    assessment_id: input.assessmentId,
    question_id: input.questionId,
    response: input.response,
    evidence_url: input.evidenceUrl.trim() || null,
    comments: input.comments.trim() || null,
    answered_by: input.userId,
  })
  if (error) throw error
}

export async function createAction(assessmentId: string, questionId: string, title: string, userId: string): Promise<void> {
  const { error } = await getSupabaseClient().from('readiness_actions').insert({
    assessment_id: assessmentId,
    question_id: questionId,
    title,
    created_by: userId,
  })
  if (error) throw error
}

export async function setActionStatus(action: ReadinessAction, completed: boolean): Promise<void> {
  const { error } = await getSupabaseClient().from('readiness_actions').update({
    status: completed ? 'completed' : 'open',
    completed_at: completed ? new Date().toISOString() : null,
  }).eq('id', action.id)
  if (error) throw error
}
